import React, { useEffect, useState } from 'react'
import Layout from '../../Components/Layout'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { FaStar, FaCar, FaUsers, FaClock, FaMapMarkerAlt, FaShieldAlt, FaWifi, FaSnowflake } from "react-icons/fa";
import config from '../../config';
import Swal from 'sweetalert2';
import GoogleMapsLoader from '../../Components/Autocomplete/GoogleMapsLoader';

export default function TransferDetail() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [transfer, setTransfer] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);
    
    const formParams = {
        type: searchParams.get('type') || 'one-way',
        pickup: searchParams.get('pickup') || '',
        dropoff: searchParams.get('dropoff') || '',
        date: searchParams.get('date') || '',
        returnDate: searchParams.get('returnDate') || '',
        adults: parseInt(searchParams.get('adults')) || 2,
        children: parseInt(searchParams.get('children')) || 0
    };

    // Static transfer data
    const staticTransferData = {
        1: {
            id: 1,
            name: "Premium Sedan Transfer",
            vehicleType: "Sedan",
            capacity: 3,
            luggage: 2,
            price: 45,
            currency: "EUR",
            duration: "45 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            images: [
                "/assets/img/cars/sedan.jpg",
                "/assets/img/cars/sedan-2.jpg",
                "/assets/img/cars/sedan-3.jpg"
            ],
            features: ["Air Conditioning", "WiFi", "Professional Driver", "GPS Navigation", "Child Seats Available"],
            rating: 4.8,
            reviews: 245,
            provider: "Premium Transfers",
            description: "Experience comfort and reliability with our premium sedan transfer service. Our professional drivers ensure a smooth and safe journey to your destination.",
            detailedDescription: "Our premium sedan transfer service offers the perfect balance of comfort, style, and affordability. Each vehicle in our fleet is meticulously maintained and equipped with modern amenities to ensure your journey is as comfortable as possible. Our experienced drivers are professionally trained, licensed, and have extensive knowledge of local routes to guarantee timely arrivals.",
            vehicleDetails: {
                make: "Mercedes-Benz",
                model: "E-Class",
                year: 2022,
                color: "Black",
                fuelType: "Hybrid",
                transmission: "Automatic"
            },
            amenities: [
                { icon: <FaSnowflake />, name: "Air Conditioning", description: "Climate-controlled interior" },
                { icon: <FaWifi />, name: "Free WiFi", description: "Complimentary internet access" },
                { icon: <FaShieldAlt />, name: "Insurance", description: "Fully insured vehicle" },
                { icon: <FaCar />, name: "GPS Navigation", description: "Real-time traffic updates" }
            ],
            cancellationPolicy: "Free cancellation up to 24 hours before pickup time",
            driverInfo: {
                name: "Professional Driver",
                experience: "5+ years",
                languages: ["English", "Turkish"],
                rating: 4.9
            },
            route: {
                distance: "35 km",
                estimatedTime: "45 minutes",
                stops: 0
            }
        },
        2: {
            id: 2,
            name: "Luxury SUV Transfer",
            vehicleType: "SUV",
            capacity: 6,
            luggage: 4,
            price: 75,
            currency: "EUR",
            duration: "45 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            images: [
                "/assets/img/cars/suv.jpg",
                "/assets/img/cars/suv-2.jpg",
                "/assets/img/cars/suv-3.jpg"
            ],
            features: ["Air Conditioning", "WiFi", "Professional Driver", "Leather Seats", "Refreshments"],
            rating: 4.9,
            reviews: 189,
            provider: "Luxury Transfers",
            description: "Travel in style with our luxury SUV transfer service. Spacious interior and premium amenities for the ultimate comfort experience.",
            detailedDescription: "Our luxury SUV transfer service is designed for those who appreciate the finer things in life. With spacious leather seating, premium sound system, and complimentary refreshments, every journey becomes a memorable experience. Perfect for families or small groups who want to travel together in comfort and style.",
            vehicleDetails: {
                make: "BMW",
                model: "X5",
                year: 2023,
                color: "White",
                fuelType: "Diesel",
                transmission: "Automatic"
            },
            amenities: [
                { icon: <FaSnowflake />, name: "Climate Control", description: "Dual-zone climate control" },
                { icon: <FaWifi />, name: "Premium WiFi", description: "High-speed internet" },
                { icon: <FaShieldAlt />, name: "Full Insurance", description: "Comprehensive coverage" },
                { icon: <FaCar />, name: "Premium Sound", description: "Surround sound system" }
            ],
            cancellationPolicy: "Free cancellation up to 24 hours before pickup time",
            driverInfo: {
                name: "Premium Driver",
                experience: "8+ years",
                languages: ["English", "Turkish", "German"],
                rating: 4.9
            },
            route: {
                distance: "35 km",
                estimatedTime: "45 minutes",
                stops: 0
            }
        }
        // Add more transfer data as needed
    };

    useEffect(() => {
        setIsLoading(true);
        // Simulate API call
        setTimeout(() => {
            const transferData = staticTransferData[id];
            if (transferData) {
                setTransfer(transferData);
            }
            setIsLoading(false);
        }, 1000);
    }, [id]);

    const handleBookNow = () => {
        Swal.fire({
            title: 'Confirm Booking',
            html: `
                <div class="text-start">
                    <p><strong>Transfer:</strong> ${transfer.name}</p>
                    <p><strong>Type:</strong> ${formParams.type}</p>
                    <p><strong>From:</strong> ${formParams.pickup}</p>
                    <p><strong>To:</strong> ${formParams.dropoff}</p>
                    <p><strong>Date:</strong> ${formParams.date}</p>
                    ${formParams.returnDate ? `<p><strong>Return:</strong> ${formParams.returnDate}</p>` : ''}
                    <p><strong>Passengers:</strong> ${formParams.adults} Adults, ${formParams.children} Children</p>
                    <p><strong>Price:</strong> ${transfer.price} ${transfer.currency}</p>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Confirm Booking',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire(
                    'Booked!',
                    'Your transfer has been booked successfully.',
                    'success'
                );
                // Here you would normally redirect to payment or confirmation page
                navigate('/transfers');
            }
        });
    };

    const StaticGoogleMap = () => {
        const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=41.0082,28.9784&zoom=12&size=600x300&maptype=roadmap&markers=color:red%7C41.0082,28.9784&key=${config.API_KEY}`;

        return (
            <img 
                src={mapUrl} 
                alt="Transfer Route Map" 
                style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '8px' }}
            />
        );
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ marginBlock: '100px' }}>
                    <img src="/assets/gif/loading.gif" alt="Loading" style={{ width: "80px", height: "80px" }} />
                    <p className="mt-3 mb-0">Loading Transfer Details...</p>
                </div>
            </Layout>
        );
    }

    if (!transfer) {
        return (
            <Layout>
                <div className="container mt-5">
                    <div className="alert alert-danger" role="alert">
                        Transfer not found!
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="breadcrumb-bar breadcrumb-bg-01 text-center">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">{transfer.name}</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center mb-0">
                                    <li className="breadcrumb-item"><a href="/"><i className="isax isax-home5"></i></a></li>
                                    <li className="breadcrumb-item"><a href="/transfers">Transfers</a></li>
                                    <li className="breadcrumb-item active" aria-current="page">Transfer Detail</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8">
                            {/* Transfer Images */}
                            <div className="card mb-4">
                                <div className="card-body p-0">
                                    <div className="transfer-gallery">
                                        <div className="main-image mb-3">
                                            <img 
                                                src={transfer.images[selectedImage]} 
                                                alt={transfer.name}
                                                className="img-fluid rounded"
                                                style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                                            />
                                        </div>
                                        <div className="thumbnail-images d-flex gap-2 px-3 pb-3">
                                            {transfer.images.map((image, index) => (
                                                <img
                                                    key={index}
                                                    src={image}
                                                    alt={`${transfer.name} ${index + 1}`}
                                                    className={`img-thumbnail cursor-pointer ${selectedImage === index ? 'border-primary' : ''}`}
                                                    style={{ width: '80px', height: '60px', objectFit: 'cover', cursor: 'pointer' }}
                                                    onClick={() => setSelectedImage(index)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Transfer Information */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h4 className="card-title mb-0">Transfer Information</h4>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-center mb-3">
                                                <FaUsers className="text-primary me-3" size={20} />
                                                <div>
                                                    <strong>Capacity:</strong> Up to {transfer.capacity} passengers
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mb-3">
                                                <FaClock className="text-primary me-3" size={20} />
                                                <div>
                                                    <strong>Duration:</strong> {transfer.duration}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-center mb-3">
                                                <i className="isax isax-bag text-primary me-3" style={{fontSize: '20px'}}></i>
                                                <div>
                                                    <strong>Luggage:</strong> {transfer.luggage} pieces
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mb-3">
                                                <FaCar className="text-primary me-3" size={20} />
                                                <div>
                                                    <strong>Vehicle Type:</strong> {transfer.vehicleType}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h5>Description</h5>
                                        <p className="text-muted">{transfer.detailedDescription}</p>
                                    </div>

                                    <div className="mb-4">
                                        <h5>Vehicle Details</h5>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p><strong>Make & Model:</strong> {transfer.vehicleDetails.make} {transfer.vehicleDetails.model}</p>
                                                <p><strong>Year:</strong> {transfer.vehicleDetails.year}</p>
                                                <p><strong>Color:</strong> {transfer.vehicleDetails.color}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p><strong>Fuel Type:</strong> {transfer.vehicleDetails.fuelType}</p>
                                                <p><strong>Transmission:</strong> {transfer.vehicleDetails.transmission}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h5>Features & Amenities</h5>
                                        <div className="row">
                                            {transfer.amenities.map((amenity, index) => (
                                                <div key={index} className="col-md-6 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="text-primary me-3" style={{fontSize: '20px'}}>
                                                            {amenity.icon}
                                                        </div>
                                                        <div>
                                                            <strong>{amenity.name}</strong>
                                                            <p className="text-muted mb-0 small">{amenity.description}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h5>Route Information</h5>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <p><strong>Distance:</strong> {transfer.route.distance}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <p><strong>Estimated Time:</strong> {transfer.route.estimatedTime}</p>
                                            </div>
                                            <div className="col-md-4">
                                                <p><strong>Stops:</strong> {transfer.route.stops}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <h5>Driver Information</h5>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p><strong>Experience:</strong> {transfer.driverInfo.experience}</p>
                                                <p><strong>Languages:</strong> {transfer.driverInfo.languages.join(', ')}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center">
                                                    <FaStar className="text-warning me-1" />
                                                    <span>{transfer.driverInfo.rating} Driver Rating</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="alert alert-info">
                                        <h6><i className="isax isax-info-circle me-2"></i>Cancellation Policy</h6>
                                        <p className="mb-0">{transfer.cancellationPolicy}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Route Map */}
                            <div className="card mb-4">
                                <div className="card-header">
                                    <h4 className="card-title mb-0">Route Map</h4>
                                </div>
                                <div className="card-body">
                                    <GoogleMapsLoader apiKey={config.API_KEY}>
                                        <StaticGoogleMap />
                                    </GoogleMapsLoader>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-4">
                            {/* Booking Summary */}
                            <div className="card sticky-top" style={{ top: '20px' }}>
                                <div className="card-header">
                                    <h4 className="card-title mb-0">Booking Summary</h4>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="d-flex align-items-center">
                                            <FaStar className="text-warning me-1" />
                                            <span>{transfer.rating}</span>
                                            <span className="text-muted ms-1">({transfer.reviews} reviews)</span>
                                        </div>
                                        <span className="badge bg-primary">{transfer.provider}</span>
                                    </div>

                                    <div className="transfer-details mb-4">
                                        <h6>Transfer Details</h6>
                                        <div className="border rounded p-3 mb-3">
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="isax isax-location text-primary me-2"></i>
                                                <div>
                                                    <strong>From:</strong> {formParams.pickup}
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="isax isax-location text-primary me-2"></i>
                                                <div>
                                                    <strong>To:</strong> {formParams.dropoff}
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="isax isax-calendar text-primary me-2"></i>
                                                <div>
                                                    <strong>Date:</strong> {formParams.date}
                                                </div>
                                            </div>
                                            {formParams.returnDate && (
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="isax isax-calendar text-primary me-2"></i>
                                                    <div>
                                                        <strong>Return:</strong> {formParams.returnDate}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="d-flex align-items-center">
                                                <FaUsers className="text-primary me-2" />
                                                <div>
                                                    <strong>Passengers:</strong> {formParams.adults} Adults, {formParams.children} Children
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="price-summary mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span>Transfer Price</span>
                                            <span>{transfer.price} {transfer.currency}</span>
                                        </div>
                                        {formParams.type === 'round-trip' && (
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span>Return Transfer</span>
                                                <span>{transfer.price} {transfer.currency}</span>
                                            </div>
                                        )}
                                        <hr />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <strong>Total Price</strong>
                                            <strong className="text-primary">
                                                {formParams.type === 'round-trip' ? transfer.price * 2 : transfer.price} {transfer.currency}
                                            </strong>
                                        </div>
                                    </div>

                                    <div className="d-grid">
                                        <button 
                                            className="btn btn-primary btn-lg"
                                            onClick={handleBookNow}
                                        >
                                            Book Now
                                        </button>
                                    </div>

                                    <div className="text-center mt-3">
                                        <small className="text-muted">
                                            <i className="isax isax-shield-tick me-1"></i>
                                            Secure booking with instant confirmation
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}
